import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { jwtDecode } from 'jwt-decode';

import { callWithAuth } from '../sagaApiHelper';
import { addProductsApi, getProductDataApi } from '../../api/apiLists/addProductApi';
import { showAlert } from '../alertSlice';
import { clearFiles, clearImageChanges } from '../filesSlice';
import { editProductsApi } from '../../api/apiLists/editProductApi';
import {
  setProductId,
  setProductData,
  setProductDataLoading,
  setProductDataError,
  setComplaintProductData,
  setComplaintProductLoading,
  setComplaintProductError,
} from '../productSlice';
import { hideLoader, showLoader } from '../loaderSlice';
import { formatComplaintProductData } from '../../utils/dataFormatters';
import { resetListData } from '../listSlice';
import { listPath } from '../../utils/constants/insightsPath';

import { handleFileUpload } from './fileUploadSaga';

function* handleAddProduct(action) {

  try {
    yield put(showLoader());
    const { imagePath, ...otherPayload } = action.payload;
    const uploadedFiles = yield call(handleFileUpload, { path: imagePath, customerId: otherPayload.customerId });
    // console.log("uploadedFiles", uploadedFiles);
    const productImages = uploadedFiles ? uploadedFiles.map(file => ({
      name: file.name,
      url: file.fileURL
    })) : [];
    const finalPayload = {
      ...otherPayload,
      productImages,
    };

    const res = yield call(callWithAuth, addProductsApi, finalPayload);
    // console.log("res", res);
    if (res?.data?.id) {
      yield delay(3000);
      yield put(clearFiles({ key: imagePath }));
      yield put(clearImageChanges({ key: imagePath }));
      yield put(setProductId(res?.data?.id));
      yield put(showAlert({ message: "Product Registered Successfully", type: 'success' }));
    } else {
      yield put(showAlert({ message: res.msg ? res.msg : 'Error Occured Please Try Again.', type: 'error' }));
    }
    yield put(hideLoader());
  } catch (error) {
    console.error("Error fetching list data:", error);
    yield put(showAlert({ message: "Error Occured Please Try Again.", type: 'error' }));
    yield put(hideLoader());
  }
}

function* handleEditProduct(action) {

  try {
    yield put(showLoader());
    // console.log('Payload : ', action.payload);
    const { imagePath, id, userId, removedImageUrls, ...otherPayload } =
      action.payload;

  let addedProductImages = [];

    if (otherPayload?.registrationFlow === 'QRCODE') {
      yield put(showAlert({ message: 'This product was registered via QR code and cannot be edited.', type: 'error' }));
      yield put(hideLoader());
      return;
    }

    // Handle file upload if there are new images
    if (imagePath) {
      const uploadedFiles = yield call(handleFileUpload, {
        path: imagePath,
        customerId: otherPayload.customerId,
      });
      // console.log('uploadedFiles for edit', uploadedFiles);
      addedProductImages = uploadedFiles?.map((file) => ({
        name: file.name,
        url: file.fileURL,
      }));
    }

    const idToken = yield select((state) => state.auth?.user?.tokens?.idToken);
    const claims = jwtDecode(idToken.toString());
    const companyId = claims['custom:companyId'];

    const finalPayload = {
      id,
      userId,
      // include companyId except when registrationFlow is QRCODE
      ...(otherPayload?.registrationFlow !== 'QRCODE' && companyId ? { companyId } : {}),
      apiVerb: 'UPDATE',
      ...(otherPayload.name && { name: otherPayload.name }),
      ...(otherPayload.category && { category: otherPayload.category }),
      ...(otherPayload.subCategory && { subCategory: otherPayload.subCategory }),
      ...(otherPayload.serialNo && { serialNo: otherPayload.serialNo }),
      ...(otherPayload.purchaseDate && {
        purchaseDate:
          typeof otherPayload.purchaseDate === 'number'
            ? otherPayload.purchaseDate
            : Math.floor(new Date(otherPayload.purchaseDate).getTime() / 1000),
      }),
      ...(otherPayload.warrantyDurationInDays && { warrantyDurationInDays: otherPayload.warrantyDurationInDays }),
      ...(otherPayload.extWarrantyDurationInDays && { extWarrantyDurationInDays: otherPayload.extWarrantyDurationInDays }),
      ...(otherPayload.skuId && { skuId: otherPayload.skuId }),
      ...(addedProductImages?.length > 0 && { addedProductImages: addedProductImages }),
      ...(removedImageUrls?.length > 0 && { removedProductImages: removedImageUrls }),
    };

    // console.log('Final payload for edit product:', finalPayload);
    const res = yield call(callWithAuth, editProductsApi, finalPayload);
  yield delay(3000);

    if (res?.status === true || res?.data?.id) {
      if (imagePath) {
        yield put(clearFiles({ key: imagePath }));
        yield put(clearImageChanges({ key: imagePath }));
      }
      yield put(setProductId(res?.data?.id || id));
      yield put(
        showAlert({ message: 'Product Updated Successfully', type: 'success' }),
      );
      yield put(resetListData(listPath.PRODUCT));
    } else {
      yield put(
        showAlert({
          message: res.msg ? res.msg : 'Error Occurred Please Try Again.',
          type: 'error',
        }),
      );
    }
    yield put(hideLoader());
  } catch (error) {
    console.error('Error editing product:', error);
    yield put(
      showAlert({ message: 'Error Occurred Please Try Again.', type: 'error' }),
    );
    yield put(hideLoader());
  }
}

function* handleGetProductData(action) {
  try {
    // set loading state in slice
    yield put(setProductDataLoading(true));

    const finalPayload = {
      id: action.payload.productId || action.payload.id,
    };

    const res = yield call(callWithAuth, getProductDataApi, finalPayload);

    if (res?.status === true) {
      // Get categories from store for formatting
      const { productCategories = [], productSubcategories = {}, models = [] } = yield select(
        (state) => state.categories || {}
      );

      // Format the product data (handles both image sources)
      const formattedData = formatComplaintProductData(
        res.data,
        productCategories,
        productSubcategories,
        models
      );

      // set product data in slice with formatted data
      yield put(setProductData(formattedData));
      yield put(setProductDataLoading(false));
    } else {
      yield put(setProductDataError(res.msg || 'Failed to fetch product data'));
      yield put(setProductDataLoading(false));
    }
  } catch (error) {
    console.error('Error fetching product data:', error);
    yield put(setProductDataError('Error occurred while fetching product data'));
  }
}

function* handleFetchComplaintProductData(action) {
  try {
    // set loading state in slice
    yield put(setComplaintProductLoading(true));

    const finalPayload = {
      id: action.payload.productId,
    };

    const res = yield call(callWithAuth, getProductDataApi, finalPayload);

    if (res?.status === true) {
      // Get categories from store for formatting
      const { productCategories = [], productSubcategories = {}, models = [] } = yield select(
        (state) => state.categories || {}
      );

      // Format the product data
      const formattedData = formatComplaintProductData(
        res.data,
        productCategories,
        productSubcategories,
        models
      );

      yield put(setComplaintProductData(formattedData));
    } else {
      yield put(setComplaintProductError(res.msg || 'Failed to fetch product data'));
    }
  } catch (error) {
    console.error('Error fetching complaint product data:', error);
    yield put(setComplaintProductError('Error occurred while fetching product data'));
  }
}

export default function* productSaga() {
  yield takeLatest('ADD_NEW_PRODUCT', handleAddProduct);
  yield takeLatest('EDIT_PRODUCT', handleEditProduct);
  yield takeLatest('GET_PRODUCT_IMAGES', handleGetProductData);
  yield takeLatest('FETCH_COMPLAINT_PRODUCT_DATA', handleFetchComplaintProductData);
}
